import UserPage from 'flarum/forum/components/UserPage';
import app from 'flarum/forum/app';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import CupExchangeModal from './CupExchangeModal';

/**
 * CreditPage — 个人积分管理页面（UserPage 子页面）
 *
 * 路由：/u/{username}/credits
 * 显示在个人主页右侧内容区，保持左侧导航栏
 */
export default class CreditPage extends UserPage {
  oninit(vnode) {
    super.oninit(vnode);

    this.loading = true;
    this.error = null;
    this.creditData = null;
    this.logs = [];
    this.logsLoading = true;

    this.rechargeCode = '';
    this.recharging = false;
    this.rechargeMsg = null;
    this.rechargeSuccess = false;

    // 流水分页
    this.page = 1;
    this.pageSize = 10;

    // 支持两种路由：/u/:username/credits 和旧版 /taro/credits
    const username = m.route.param('username') || (app.session.user ? app.session.user.slug() : null);
    if (username) {
      this.loadUser(username);
    }
  }

  /** UserPage 加载完用户后调用 */
  show(user) {
    super.show(user);
    this.loadCreditData();
    this.loadLogs();
  }

  /** 从 JSON:API 响应中提取 attributes */
  extractAttrs(data) {
    if (!data) return null;
    if (data.data) {
      return data.data.attributes || data.data;
    }
    return data;
  }

  async loadCreditData() {
    const api = app.forum.attribute('apiUrl');
    const userId = app.session.user ? app.session.user.id() : null;
    if (!userId) {
      this.error = '请先登录';
      this.loading = false;
      m.redraw();
      return;
    }

    try {
      const res = await app.request({
        method: 'GET',
        url: `${api}/taro/credits/${userId}`,
      });
      this.creditData = this.extractAttrs(res);
    } catch (e) {
      this.error = e.message || '加载积分数据失败';
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  async loadLogs() {
    const api = app.forum.attribute('apiUrl');
    try {
      const res = await app.request({
        method: 'GET',
        url: `${api}/taro/credits/logs`,
      });
      const rawList = Array.isArray(res) ? res : (res.data || []);
      this.logs = rawList.map(item => item.attributes || item);
    } catch (e) {
      // 不影响主页面
    } finally {
      this.logsLoading = false;
      m.redraw();
    }
  }

  async handleRecharge() {
    const code = this.rechargeCode.trim();
    if (!code) {
      this.rechargeMsg = '请输入充值码';
      this.rechargeSuccess = false;
      m.redraw();
      return;
    }

    this.recharging = true;
    this.rechargeMsg = null;
    m.redraw();

    const api = app.forum.attribute('apiUrl');
    try {
      const res = await app.request({
        method: 'POST',
        url: `${api}/taro/credits/recharge`,
        body: { code },
      });
      const attrs = this.extractAttrs(res);
      if (attrs && attrs.balance) {
        this.creditData = {
          ...this.creditData,
          balance: attrs.balance,
        };
      }
      this.rechargeSuccess = true;
      this.rechargeMsg = '充值成功！';
      this.rechargeCode = '';
      this.loadLogs();
    } catch (e) {
      let detail = '';
      try {
        const body = JSON.parse(e.response);
        detail = body.errors?.[0]?.detail || '';
      } catch (_) {}
      this.rechargeMsg = detail || e.message || '充值失败，请检查充值码';
      this.rechargeSuccess = false;
    } finally {
      this.recharging = false;
      m.redraw();
    }
  }

  // ── 格式化 ──

  formatTime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  }

  changeTypeLabel(type) {
    const map = { 'income': '收入', 'expense': '支出' };
    return map[type] || type;
  }

  creditTypeName(type) {
    const map = { 'star_coin': '星币', 'sword': '宝剑', 'wand': '权杖', 'cup': '圣杯' };
    return map[type] || type;
  }

  creditTypeIcon(type) {
    const map = {
      'star_coin': 'far fa-futbol',
      'sword': 'fas fa-pen-fancy',
      'wand': 'fas fa-magic',
      'cup': 'fas fa-wine-glass-alt',
    };
    return map[type] || 'fas fa-circle';
  }

  creditTypeMeta(type) {
    // 低饱和莫兰迪四色，与灰蓝主色协调，保留四元素辨识度
    const meta = {
      'star_coin': { color: '#B8902E', bg: '#FAF3E0', label: '星币', icon: 'far fa-futbol', desc: '通用' },
      'sword':     { color: '#5B8A5A', bg: '#EDF3EC', label: '宝剑', icon: 'fas fa-pen-fancy', desc: '学习' },
      'wand':      { color: '#B0554A', bg: '#F7ECEA', label: '权杖', icon: 'fas fa-magic', desc: '解读' },
      'cup':       { color: '#3E7CB1', bg: '#E9F1F8', label: '圣杯', icon: 'fas fa-wine-glass-alt', desc: '互动' },
    };
    return meta[type] || { color: '#9EA7B0', bg: '#F0F2F5', label: type, icon: 'fas fa-circle', desc: '' };
  }

  totalCredits() {
    if (!this.creditData || !this.creditData.balance) return 0;
    return Object.values(this.creditData.balance).reduce((sum, v) => sum + (v || 0), 0);
  }

  // ── 流水分页辅助 ──

  setPage(p) {
    this.page = Math.max(1, Math.min(this.totalPages(), p));
    m.redraw();
  }

  totalPages() {
    return Math.max(1, Math.ceil(this.logs.length / this.pageSize));
  }

  pagedLogs() {
    const start = (this.page - 1) * this.pageSize;
    return this.logs.slice(start, start + this.pageSize);
  }

  visiblePages() {
    const total = this.totalPages();
    const cur = this.page;
    const pages = [];
    const push = (n) => { if (n >= 1 && n <= total && !pages.includes(n)) pages.push(n); };
    push(1);
    push(2);
    if (cur > 4) pages.push('…');
    for (let i = Math.max(3, cur - 1); i <= Math.min(total - 2, cur + 1); i++) push(i);
    if (cur < total - 3) pages.push('…');
    if (total > 1) push(total - 1);
    push(total);
    return pages;
  }

  // ── 渲染（UserPage.content） ──

  content() {
    if (this.loading) {
      return (
        <div className="CreditPage CreditPage--loading">
          <LoadingIndicator />
        </div>
      );
    }

    if (this.error) {
      return (
        <div className="CreditPage">
          <div className="CreditPage-error">
            <i className="fas fa-exclamation-triangle" />
            <span>{this.error}</span>
          </div>
        </div>
      );
    }

    if (!this.creditData) return null;

    const balance = this.creditData.balance || {};
    const types = ['star_coin', 'sword', 'wand', 'cup'];

    return (
      <div className="CreditPage">
        <h2 className="CreditPage-title">我的积分</h2>

        {/* ═══ 第一行：四积分表格 ═══ */}
        <table className="CreditPage-table">
          <thead>
            <tr>
              {types.map((type) => {
                const meta = this.creditTypeMeta(type);
                return (
                  <th key={type} style={{ borderColor: meta.color }}>
                    <i className={meta.icon} style={{ color: meta.color }}></i>
                    <span>{meta.label}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr>
              {types.map((type) => {
                const meta = this.creditTypeMeta(type);
                const value = balance[type] || 0;
                return (
                  <td key={type}>
                    <div className="CreditPage-tableValue" style={{ color: meta.color }}>{value}</div>
                    <div className="CreditPage-tableDesc">{meta.desc}</div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>

        {/* ═══ 第三行：充值码快捷充值 ═══ */}
        <div className="CreditPage-panel">
          <div className="CreditPage-panel-head">
            <i className="fas fa-bolt" style={{ color: '#f59e0b' }} />
            <span>快捷充值</span>
            <span className="CreditPage-panel-hint">输入充值码立即到账</span>
          </div>
          <div className="CreditPage-panel-body">
            <div className="CreditPage-recharge">
              <input
                className="FormControl CreditPage-recharge-input"
                type="text"
                placeholder="请输入充值码..."
                value={this.rechargeCode}
                oninput={(e) => { this.rechargeCode = e.target.value; m.redraw(); }}
                disabled={this.recharging}
              />
              <Button
                className="Button Button--primary CreditPage-recharge-btn"
                onclick={() => this.handleRecharge()}
                loading={this.recharging}
                disabled={this.recharging || !this.rechargeCode.trim()}
              >
                <i className="fas fa-paper-plane" />
                <span>立即充值</span>
              </Button>
              <Button
                className="Button Button--secondary CreditPage-recharge-btn"
                onclick={() => app.modal.show(CupExchangeModal)}
                title="圣杯收益可兑换星币"
              >
                <i className="fas fa-sync-alt" />
                <span>圣杯兑换</span>
              </Button>
            </div>
            {this.rechargeMsg && (
              <div className={'CreditPage-message CreditPage-message--' + (this.rechargeSuccess ? 'success' : 'error')}>
                <i className={'fas ' + (this.rechargeSuccess ? 'fa-check-circle' : 'fa-exclamation-circle')} />
                <span>{this.rechargeMsg}</span>
              </div>
            )}
          </div>
        </div>

        {/* ═══ 第四行：积分变动历史 ═══ */}
        <div className="CreditPage-panel">
          <div className="CreditPage-panel-head">
            <i className="fas fa-stream" />
            <span>积分变动历史</span>
            {this.logs.length > 0 && (
              <span className="CreditPage-panel-badge">共 {this.logs.length} 条</span>
            )}
          </div>
          <div className="CreditPage-panel-body CreditPage-panel-body--padded">
            {this.logsLoading ? (
              <LoadingIndicator />
            ) : this.logs.length === 0 ? (
              <div className="CreditPage-empty">
                <i className="fas fa-inbox" />
                <p>暂无积分变动记录</p>
              </div>
            ) : (
              <>
                <ul className="CreditPage-history">
                  {this.pagedLogs().map((log, i) => {
                    const meta = this.creditTypeMeta(log.creditType);
                    const change = log.amount || 0;
                    const isPositive = change > 0;
                    return (
                      <li className="CreditPage-historyItem" key={i}>
                        <div className="CreditPage-historyIcon" style={{ background: meta.bg, color: meta.color }}>
                          <i className={meta.icon}></i>
                        </div>
                        <div className="CreditPage-historyBody">
                          <div className="CreditPage-historyReason">{log.reason || this.creditTypeName(log.creditType)}</div>
                          <div className="CreditPage-historyMeta">
                            <span>{this.formatTime(log.createdAt)}</span>
                            <span className="CreditPage-historyDot">·</span>
                            <span>{meta.label}</span>
                            <span className="CreditPage-historyDot">·</span>
                            <span>{this.changeTypeLabel(log.changeType)}</span>
                          </div>
                        </div>
                        <div className={'CreditPage-historyAmount CreditPage-historyAmount--' + (isPositive ? 'plus' : 'minus')}>
                          {isPositive ? '+' : ''}{change}
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {this.totalPages() > 1 && (
                  <div className="CreditPage-pagination">
                    <button
                      className="CreditPage-pageBtn"
                      disabled={this.page === 1}
                      onclick={() => this.setPage(this.page - 1)}
                    >
                      <i className="fas fa-chevron-left" />
                      <span>上一页</span>
                    </button>

                    <div className="CreditPage-pageNums">
                      {this.visiblePages().map((p, i) => (
                        p === '…'
                          ? <span className="CreditPage-pageEllipsis" key={'e' + i}>…</span>
                          : <button
                              key={p}
                              className={'CreditPage-pageNum' + (p === this.page ? ' is-active' : '')}
                              onclick={() => this.setPage(p)}
                            >{p}</button>
                      ))}
                    </div>

                    <button
                      className="CreditPage-pageBtn"
                      disabled={this.page === this.totalPages()}
                      onclick={() => this.setPage(this.page + 1)}
                    >
                      <span>下一页</span>
                      <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}
